import React from 'react'
import { Box, Typography, Paper } from '@mui/material'
import { useAppTheme } from '@/theme/ThemeContext'

interface DetailCardProps {
  title: string
  icon?: string | React.ReactNode
  color?: string
  children: React.ReactNode
  fitContent?: boolean
}

const RUBIK =
  '"Rubik", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif'

const DetailCard: React.FC<DetailCardProps> = ({
  title,
  icon,
  children,
  fitContent = false,
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const TITLE = isDarkMode ? '#FFFFFF' : '#003F61'
  const BG_COLOR = isDarkMode ? '#202B40' : 'rgba(255, 255, 255, 0.8)'
  const BORDER_COLOR = isDarkMode ? '#374151' : '#e8e8e8'
  const DIVIDER_COLOR = isDarkMode ? '#374151' : '#f0f0f0'

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '8px',
        height: fitContent ? 'auto' : '100%',
        backgroundColor: BG_COLOR,
        border: `1px solid ${BORDER_COLOR}`,
        backdropFilter: 'blur(8px)',
        direction: 'rtl',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          borderRadius: '8px',
          px: 3,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'row-reverse',
          borderBottom: `1px solid ${DIVIDER_COLOR}`,
          gap: 1,
          direction: 'rtl',
          marginBottom: '12px',
        }}
      >
        {icon && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            '& img': { 
              filter: isDarkMode 
                ? 'brightness(0) saturate(100%) invert(45%) sepia(95%) saturate(500%) hue-rotate(65deg) brightness(95%)' 
                : 'none' 
            } 
          }}>
            {typeof icon === 'string' ? icon : icon}
          </Box>
        )}
        <Typography
          variant="h6"
          sx={{
            fontFamily: RUBIK,
            fontWeight: 400,
            fontSize: 16,
            lineHeight: 1,
            letterSpacing: 0,
            textAlign: 'right',
            color: TITLE,
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          px: 3,
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        {children}
      </Box>
    </Paper>
  )
}

export default DetailCard
